import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip } from "recharts";
import { motion } from "framer-motion";
import { 
  Users, Home, Zap, ShoppingCart, Wifi, Car, Mail, Save, 
  Sofa, Palette, UtensilsCrossed, Truck, FileText, Key, 
  Router, SprayCan, Shield, PawPrint, Plug, ClipboardCheck,
  DollarSign, MoreHorizontal, Info
} from "lucide-react";
import { toast } from "sonner";

const COLORS = ['#10B981', '#0F172A', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#6366F1'];

const ExpenseSplitCalculator = () => {
  const [roommates, setRoommates] = useState(2);
  const [splitMethod, setSplitMethod] = useState("equal");
  const [rentPercentages, setRentPercentages] = useState([50, 50]);
  const [email, setEmail] = useState("");
  const [activeTab, setActiveTab] = useState("monthly");
  
  // Monthly recurring expenses
  const [monthlyExpenses, setMonthlyExpenses] = useState({
    rent: { amount: 1800, split: true, paidBy: "shared" },
    utilities: { amount: 150, split: true, paidBy: "shared" },
    internet: { amount: 80, split: true, paidBy: "shared" },
    groceries: { amount: 400, split: false, paidBy: "you" },
    transportation: { amount: 150, split: false, paidBy: "you" },
    subscriptions: { amount: 50, split: true, paidBy: "shared" },
    insurance: { amount: 30, split: true, paidBy: "shared" },
    other: { amount: 0, split: false, paidBy: "you" },
  });
  
  // One-time move-in expenses
  const [oneTimeExpenses, setOneTimeExpenses] = useState({
    furniture: { amount: 0, split: true, paidBy: "shared" },
    decor: { amount: 0, split: true, paidBy: "shared" },
    kitchenware: { amount: 0, split: true, paidBy: "shared" },
    movingCosts: { amount: 0, split: true, paidBy: "shared" },
    applicationFees: { amount: 0, split: true, paidBy: "shared" },
    keyFees: { amount: 0, split: true, paidBy: "shared" },
    internetSetup: { amount: 0, split: true, paidBy: "shared" },
    cleaningSupplies: { amount: 0, split: true, paidBy: "shared" },
    securityDeposit: { amount: 0, split: true, paidBy: "shared" },
    petDeposit: { amount: 0, split: false, paidBy: "you" },
    utilityDeposits: { amount: 0, split: true, paidBy: "shared" },
    rentersInsurance: { amount: 0, split: true, paidBy: "shared" },
  });

  const monthlyConfig = [
    { key: "rent", label: "Rent", icon: Home, color: "#10B981" },
    { key: "utilities", label: "Utilities", icon: Zap, color: "#F59E0B" },
    { key: "internet", label: "Internet/Phone", icon: Wifi, color: "#8B5CF6" },
    { key: "groceries", label: "Groceries", icon: ShoppingCart, color: "#EF4444" },
    { key: "transportation", label: "Transportation", icon: Car, color: "#06B6D4" },
    { key: "subscriptions", label: "Subscriptions", icon: Wifi, color: "#0F172A" },
    { key: "insurance", label: "Insurance", icon: Shield, color: "#EC4899" },
    { key: "other", label: "Other", icon: MoreHorizontal, color: "#6366F1" },
  ];
  
  const oneTimeConfig = [
    { key: "furniture", label: "Furniture", icon: Sofa, color: "#10B981" },
    { key: "decor", label: "Decor", icon: Palette, color: "#F59E0B" },
    { key: "kitchenware", label: "Cooking Equipment & Kitchenware", icon: UtensilsCrossed, color: "#8B5CF6" },
    { key: "movingCosts", label: "Moving Costs (truck, movers, boxes)", icon: Truck, color: "#EF4444" },
    { key: "applicationFees", label: "Application / Lease Fees", icon: FileText, color: "#06B6D4" },
    { key: "keyFees", label: "Key / Lock / Fob Fees", icon: Key, color: "#0F172A" },
    { key: "internetSetup", label: "Internet Setup & Router", icon: Router, color: "#EC4899" },
    { key: "cleaningSupplies", label: "Cleaning Supplies & Basics", icon: SprayCan, color: "#6366F1" },
    { key: "securityDeposit", label: "Rental / Security Deposit", icon: DollarSign, color: "#10B981" },
    { key: "petDeposit", label: "Pet Deposit (if applicable)", icon: PawPrint, color: "#F59E0B" },
    { key: "utilityDeposits", label: "Utility Deposits (hydro, gas)", icon: Plug, color: "#8B5CF6" },
    { key: "rentersInsurance", label: "Tenant / Renters Insurance", icon: ClipboardCheck, color: "#EF4444" },
  ];

  // Update percentages when roommates change
  const updateRoommates = (num) => {
    setRoommates(num);
    const equalShare = Math.floor(100 / num);
    const newPercentages = Array(num).fill(equalShare);
    newPercentages[0] += 100 - (equalShare * num);
    setRentPercentages(newPercentages);
  };

  // Calculate expense share based on rules
  const calculateShare = (expense, key, isRent = false) => {
    if (roommates === 1) return expense.amount;
    
    // If not shared (paid by one person)
    if (!expense.split || expense.paidBy !== "shared") {
      return expense.paidBy === "you" ? expense.amount : 0;
    }
    
    // If shared
    if (isRent && splitMethod === "percentage") {
      // Only rent uses custom percentages
      return expense.amount * (rentPercentages[0] / 100);
    } else {
      // All other shared expenses are 50/50
      return expense.amount / roommates;
    }
  };

  const calculations = useMemo(() => {
    // Monthly calculations
    let monthlyTotal = 0;
    const monthlyBreakdown = monthlyConfig.map(({ key, label, color }) => {
      const exp = monthlyExpenses[key];
      const isRent = key === "rent";
      const yourAmount = calculateShare(exp, key, isRent);
      monthlyTotal += yourAmount;
      return {
        name: label,
        value: Math.round(yourAmount),
        total: exp.amount,
        split: exp.split,
        paidBy: exp.paidBy,
        color
      };
    });
    
    // One-time calculations
    let oneTimeTotal = 0;
    const oneTimeBreakdown = oneTimeConfig.map(({ key, label, color }) => {
      const exp = oneTimeExpenses[key];
      const yourAmount = calculateShare(exp, key, false); // Never use custom % for one-time
      oneTimeTotal += yourAmount;
      return {
        name: label,
        value: Math.round(yourAmount),
        total: exp.amount,
        split: exp.split,
        paidBy: exp.paidBy,
        color
      };
    });
    
    // Calculate total if living alone (for savings comparison)
    const totalSharedMonthly = Object.values(monthlyExpenses)
      .filter(exp => exp.split)
      .reduce((sum, exp) => sum + exp.amount, 0);
    
    const totalSharedOneTime = Object.values(oneTimeExpenses)
      .filter(exp => exp.split)
      .reduce((sum, exp) => sum + exp.amount, 0);

    return {
      monthlyTotal: Math.round(monthlyTotal),
      monthlyAnnual: Math.round(monthlyTotal * 12),
      oneTimeTotal: Math.round(oneTimeTotal),
      grandTotal: Math.round(monthlyTotal + oneTimeTotal),
      monthlyBreakdown: monthlyBreakdown.filter(b => b.value > 0 || b.total > 0),
      oneTimeBreakdown: oneTimeBreakdown.filter(b => b.value > 0 || b.total > 0),
      monthlySavings: roommates > 1 ? Math.round(totalSharedMonthly - (totalSharedMonthly / roommates)) : 0,
      oneTimeSavings: roommates > 1 ? Math.round(totalSharedOneTime - (totalSharedOneTime / roommates)) : 0,
    };
  }, [monthlyExpenses, oneTimeExpenses, roommates, splitMethod, rentPercentages]);

  const handleMonthlyChange = (key, value) => {
    setMonthlyExpenses(prev => ({
      ...prev,
      [key]: { ...prev[key], amount: parseInt(value) || 0 }
    }));
  };

  const handleOneTimeChange = (key, value) => {
    setOneTimeExpenses(prev => ({
      ...prev,
      [key]: { ...prev[key], amount: parseInt(value) || 0 }
    }));
  };

  const handleMonthlySplitChange = (key, paidBy) => {
    setMonthlyExpenses(prev => ({
      ...prev,
      [key]: { 
        ...prev[key], 
        split: paidBy === "shared",
        paidBy 
      }
    }));
  };

  const handleOneTimeSplitChange = (key, paidBy) => {
    setOneTimeExpenses(prev => ({
      ...prev,
      [key]: { 
        ...prev[key], 
        split: paidBy === "shared",
        paidBy 
      }
    }));
  };

  const handlePercentageChange = (index, value) => {
    const newPercentages = [...rentPercentages];
    newPercentages[index] = value;
    const remaining = 100 - value;
    const otherCount = roommates - 1;
    for (let i = 0; i < roommates; i++) {
      if (i !== index) {
        newPercentages[i] = Math.round(remaining / otherCount);
      }
    }
    setRentPercentages(newPercentages);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const handleSave = () => {
    toast.success("Your expense breakdown has been saved!");
  };

  const handleEmail = () => {
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    toast.success("Your expense plan has been sent to your email!");
    setEmail("");
  };

  return (
    <Card className="border-0 shadow-floating" data-testid="expense-calculator">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Users className="w-6 h-6 text-emerald-500" />
          Roommate Expense Calculator
        </CardTitle>
        <p className="text-sm text-slate-500">
          Split monthly expenses and one-time move-in costs fairly with roommates
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Roommates Selection */}
        <div className="space-y-3">
          <Label>Number of Roommates (including you)</Label>
          <div className="flex items-center gap-4">
            <Slider
              value={[roommates]}
              onValueChange={(v) => updateRoommates(v[0])}
              min={1}
              max={6}
              step={1}
              className="flex-1"
            />
            <div className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-lg font-bold min-w-[80px] text-center">
              {roommates === 1 ? "Just Me" : `${roommates} people`}
            </div>
          </div>
        </div>

        {/* Split Method - Only affects RENT */}
        {roommates > 1 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label>Rent Split Method</Label>
              <Badge variant="outline" className="text-xs">Only affects rent</Badge>
            </div>
            <Tabs value={splitMethod} onValueChange={setSplitMethod}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="equal">Equal Split (50/50)</TabsTrigger>
                <TabsTrigger value="percentage">Custom Percentages</TabsTrigger>
              </TabsList>
              
              {splitMethod === "percentage" && (
                <TabsContent value="percentage" className="mt-4">
                  <div className="bg-blue-50 rounded-lg p-3 mb-3">
                    <p className="text-xs text-blue-700 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Custom percentages only apply to rent. All other shared expenses are split 50/50.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {Array.from({ length: roommates }).map((_, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <span className="text-sm text-slate-600 w-24">
                          {idx === 0 ? "You" : `Person ${idx + 1}`}
                        </span>
                        <Slider
                          value={[rentPercentages[idx] || 0]}
                          onValueChange={(v) => handlePercentageChange(idx, v[0])}
                          min={0}
                          max={100}
                          step={5}
                          className="flex-1"
                        />
                        <span className="font-mono text-emerald-600 w-12 text-right">
                          {rentPercentages[idx] || 0}%
                        </span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>
        )}

        {/* Expense Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="monthly">Monthly Expenses</TabsTrigger>
            <TabsTrigger value="onetime">One-Time Move-In Costs</TabsTrigger>
          </TabsList>
          
          {/* Monthly Expenses Tab */}
          <TabsContent value="monthly" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {monthlyConfig.map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="text-xs sm:text-sm">{label}</Label>
                    <Input
                      type="text"
                      value={formatCurrency(monthlyExpenses[key].amount)}
                      onChange={(e) => handleMonthlyChange(key, e.target.value.replace(/[^0-9]/g, ''))}
                      className="h-8 mt-1"
                    />
                  </div>
                  {roommates > 1 && (
                    <div className="flex-shrink-0 w-24 sm:w-28">
                      <Select
                        value={monthlyExpenses[key].paidBy}
                        onValueChange={(value) => handleMonthlySplitChange(key, value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="shared">Shared 50/50</SelectItem>
                          <SelectItem value="you">I pay</SelectItem>
                          <SelectItem value="other">Roommate pays</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
          
          {/* One-Time Expenses Tab */}
          <TabsContent value="onetime" className="mt-4">
            <div className="bg-amber-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-700">
                <strong>Move-in costs:</strong> Enter any one-time expenses for setting up your place. 
                These are calculated separately from monthly expenses.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-2">
              {oneTimeConfig.map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="text-xs">{label}</Label>
                    <Input
                      type="text"
                      value={formatCurrency(oneTimeExpenses[key].amount)}
                      onChange={(e) => handleOneTimeChange(key, e.target.value.replace(/[^0-9]/g, ''))}
                      className="h-8 mt-1"
                    />
                  </div>
                  {roommates > 1 && (
                    <div className="flex-shrink-0 w-24 sm:w-28">
                      <Select
                        value={oneTimeExpenses[key].paidBy}
                        onValueChange={(value) => handleOneTimeSplitChange(key, value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="shared">Shared 50/50</SelectItem>
                          <SelectItem value="you">I pay</SelectItem>
                          <SelectItem value="other">Roommate pays</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Results */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          {/* Summary Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-emerald-50 rounded-xl p-4 text-center">
              <p className="text-sm text-emerald-600">Your Monthly</p>
              <motion.p 
                key={calculations.monthlyTotal}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="text-2xl font-bold text-emerald-700"
              >
                {formatCurrency(calculations.monthlyTotal)}
              </motion.p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <p className="text-sm text-purple-600">Your One-Time</p>
              <motion.p 
                key={calculations.oneTimeTotal}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="text-2xl font-bold text-purple-700"
              >
                {formatCurrency(calculations.oneTimeTotal)}
              </motion.p>
            </div>
            <div className="bg-navy-700 rounded-xl p-4 text-center">
              <p className="text-sm text-slate-300">Annual Total</p>
              <motion.p 
                key={calculations.monthlyAnnual}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="text-2xl font-bold text-white"
              >
                {formatCurrency(calculations.monthlyAnnual)}
              </motion.p>
            </div>
            {roommates > 1 && (
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-sm text-amber-600">Monthly Savings</p>
                <motion.p 
                  key={calculations.monthlySavings}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="text-2xl font-bold text-amber-700"
                >
                  {formatCurrency(calculations.monthlySavings)}
                </motion.p>
                <p className="text-xs text-amber-500">vs. living alone</p>
              </div>
            )}
          </div>

          {/* Visual Breakdown */}
          {activeTab === "monthly" && calculations.monthlyBreakdown.length > 0 && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div>
                <h4 className="font-heading font-bold mb-4">Monthly Breakdown</h4>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={calculations.monthlyBreakdown.filter(b => b.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {calculations.monthlyBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Bar Chart */}
              <div>
                <h4 className="font-heading font-bold mb-4">Expense Breakdown</h4>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={calculations.monthlyBreakdown.filter(b => b.value > 0)} layout="vertical">
                      <XAxis type="number" tickFormatter={(v) => `$${v}`} />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                      <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                      <Bar dataKey="value" name="Your Share">
                        {calculations.monthlyBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Breakdown */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h4 className="font-heading font-bold mb-3">
              {activeTab === "monthly" ? "Monthly" : "One-Time"} Detailed Breakdown
            </h4>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {(activeTab === "monthly" ? calculations.monthlyBreakdown : calculations.oneTimeBreakdown)
                .filter(item => item.total > 0)
                .map((item) => (
                  <div key={item.name} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">{item.name}</span>
                      {roommates > 1 && (
                        <Badge variant="outline" className="text-xs">
                          {item.paidBy === "shared" ? "50/50" : item.paidBy === "you" ? "You" : "Roommate"}
                        </Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="font-mono font-bold text-navy-700">{formatCurrency(item.value)}</span>
                      {item.split && roommates > 1 && (
                        <span className="text-xs text-slate-400 ml-2">
                          (from {formatCurrency(item.total)})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Move-in Total Summary */}
          {calculations.oneTimeTotal > 0 && (
            <div className="bg-purple-50 rounded-xl p-4">
              <h4 className="font-heading font-bold mb-2 text-purple-700">Move-In Cost Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-sm text-purple-600">Your One-Time Costs</p>
                  <p className="text-xl font-bold text-purple-700">{formatCurrency(calculations.oneTimeTotal)}</p>
                </div>
                {roommates > 1 && (
                  <div>
                    <p className="text-sm text-purple-600">You're Saving</p>
                    <p className="text-xl font-bold text-purple-700">{formatCurrency(calculations.oneTimeSavings)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={handleSave} variant="outline" className="flex-1">
              <Save className="w-4 h-4 mr-2" />
              Save Results
            </Button>
            <div className="flex-1 flex gap-2">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              <Button onClick={handleEmail} className="bg-emerald-500 hover:bg-emerald-600">
                <Mail className="w-4 h-4 mr-2" />
                Email Plan
              </Button>
            </div>
          </div>
        </motion.div>
      </CardContent>
    </Card>
  );
};

export default ExpenseSplitCalculator;
